/**
 * Firebase Configuration for Web
 * Initializes Firebase App and Firebase Analytics
 * Supports Dev and Prod environments via REACT_APP_FIREBASE_* env vars
 * @module config/firebase
 */

import { type Analytics, getAnalytics, isSupported } from 'firebase/analytics';
import { type FirebaseApp, getApp, getApps, initializeApp } from 'firebase/app';
import { logger } from '../utils/logger';

const isProd = process.env.REACT_APP_ENVIRONMENT === 'prod';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || 'AIzaSyAYp5Q3KT2EQ-XPmYyu7YTNBbhTOcRON20',
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || 'theychat-de55e.firebaseapp.com',
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || 'theychat-de55e',
  storageBucket:
    process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || 'theychat-de55e.firebasestorage.app',
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '324441260545',
  appId:
    process.env.REACT_APP_FIREBASE_APP_ID ||
    (isProd
      ? '1:324441260545:web:27bca38c490ed5491e7600'
      : '1:324441260545:web:b738ed8e59af0b921e7600'),
  measurementId:
    process.env.REACT_APP_FIREBASE_MEASUREMENT_ID ||
    process.env.REACT_APP_GA_MEASUREMENT_ID ||
    (isProd ? 'G-53CB82LXR7' : 'G-N42CMFESCR'),
};

// Singleton Firebase App instance
export const app: FirebaseApp = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();

let analyticsInstance: Analytics | null = null;
let analyticsPromise: Promise<Analytics | null> | null = null;

/**
 * Safely get or initialize Firebase Analytics instance.
 * Checks for browser support (IndexedDB, cookies, SSR compatibility).
 */
export const getFirebaseAnalytics = async (): Promise<Analytics | null> => {
  if (typeof window === 'undefined') {
    return null;
  }

  if (analyticsInstance) {
    return analyticsInstance;
  }

  if (!analyticsPromise) {
    analyticsPromise = (async () => {
      try {
        const supported = await isSupported();
        if (supported) {
          analyticsInstance = getAnalytics(app);
          return analyticsInstance;
        }
      } catch (error) {
        logger.warn('Firebase', 'Failed to initialize analytics', error as Error);
      }
      return null;
    })();
  }

  return analyticsPromise;
};

